import React, { useState } from 'react';
import { Row, Col, Button } from 'react-bootstrap';
import './Resumen.scss';
import BillItem from './components/BillItem/BillItem';
import CreateCategoryBill from './components/CreateCategoryBill/CreateCategoryBill';
import {
  HouseFill,
  LightningFill,
  DropletFill,
  FileEarmarkTextFill,
  CurrencyDollar,
  Wifi,
  Fire
} from 'react-bootstrap-icons';
import { FaPlus, FaMousePointer, FaTimes, FaTrash } from 'react-icons/fa';
import { deleteLocalBillCategory } from '../../../../../../../../crud/management_api/local_bill_category';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

interface ResumenProps {
  billsList: any[];
  setBillsList: React.Dispatch<React.SetStateAction<any[]>>;
  globalCategoriesList: any[];
  localCategoriesList: any[];
  setLocalCategoriesList: React.Dispatch<React.SetStateAction<any[]>>;
  fetchBills: () => void; // Función para obtener las facturas
}

// Íconos para categorías globales
const globalCategoryIcons: Record<number, React.ReactNode> = {
  1: <HouseFill color="#ccc" size={30} />,
  2: <LightningFill color="#ccc" size={30} />,
  3: <DropletFill color="#ccc" size={30} />,
  4: <FileEarmarkTextFill color="#ccc" size={30} />,
  5: <Fire color="#ccc" size={30} />,
  6: <Wifi color="#ccc" size={30} />,
};

// Ícono para categorías locales
const localCategoryIcon = <CurrencyDollar color="#ccc" size={30} />;

const Resumen: React.FC<ResumenProps> = ({
  billsList,
  setBillsList,
  globalCategoriesList,
  localCategoriesList,
  setLocalCategoriesList,
  fetchBills
}) => {
  const [showCreateCategory, setShowCreateCategory] = useState(false);
  const [showSelection, setShowSelection] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<any[]>([]);

  const handleAddLocalCategory = (newCategory: { local_bill_category_id: number; category_name: string; }) => {
    setLocalCategoriesList(prev => [{ ...newCategory, total: 0 }, ...prev]);
  };

  // Actualiza una categoría local en la lista tras editar
  const handleEditLocalCategory = (editedCategory: { local_bill_category_id: number; item_name: string; item_description?: string }) => {
    setLocalCategoriesList(prev => prev.map(cat =>
      cat.local_bill_category_id === editedCategory.local_bill_category_id
        ? { ...cat, category_name: editedCategory.item_name, category_description: editedCategory.item_description }
        : cat
    ));
  };

  const handleDeleteSelectedCategories = async () => {
    let successCount = 0, errorCount = 0;
    for (const cat of selectedCategories) {
      if (cat.local_bill_category_id) {
        try {
          await deleteLocalBillCategory(cat.local_bill_category_id);
          successCount++;
        } catch {
          errorCount++;
        }
      }
    }
    setLocalCategoriesList(prev =>
      prev.filter(cat => !selectedCategories.some(sel => sel.local_bill_category_id === cat.local_bill_category_id))
    );
    setSelectedCategories([]);
    setShowSelection(false);
    if (successCount) toast.success(`Cuenta/s eliminada/s exitosamente`);
    if (errorCount) toast.error(`Error al eliminar ${errorCount} categoría(s).`);
  };

  // Agrupación global
  const globalFromBills = billsList.reduce((acc: any, bill: any) => {
    const id = bill.global_bill_category_id;
    if (id) {
      if (!acc[id]) acc[id] = { name: bill.global_bill_category_name, total: 0, global_bill_category_id: id };
      acc[id].total += bill.amount;
    }
    return acc;
  }, {});
  const combinedGlobalCategories = globalCategoriesList.map(cat => {
    const categoryId = cat.global_bill_category_id;
    return {
      id: `global-${categoryId}`,
      global_bill_category_id: categoryId,
      name: cat.category_name,
      total: globalFromBills[categoryId]?.total || 0
    };
  });

  // Agrupación local
  const localFromBills = billsList.reduce((acc: any, bill: any) => {
    const id = bill.local_bill_category_id;
    if (id) {
      if (!acc[id]) acc[id] = { name: bill.local_bill_category_name, total: 0, local_bill_category_id: id };
      acc[id].total += bill.amount;
    }
    return acc;
  }, {});
  const combinedLocalCategories = localCategoriesList.map(cat => {
    const categoryId = cat.local_bill_category_id;
    return {
      id: `local-${categoryId}`,
      local_bill_category_id: categoryId,
      name: cat.category_name,
      total: localFromBills[categoryId]?.total || 0,
      description: cat.category_description || ''
    };
  });

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdminOrSuperAdmin = user && (user.rol_id === 1 || user.rol_id === 0);

  return (
    <div className="resumen-container">

      {/* Cuentas Principales */}
      <h2 className="resumen-header">Cuentas Principales</h2>
      <Row className='bill-category-row'>
        {combinedGlobalCategories.map(category => {
          const id = category.global_bill_category_id;
          return (
            <Col key={id} lg={2} className="bill-category-col">
              <BillItem
                icon={
                  globalCategoryIcons[Number(id)] ||
                  <FileEarmarkTextFill color="#ccc" size={30} />
                }
                billsList={category}
                allBills={billsList}
                setBillsList={setBillsList}
                isAdminOrSuperAdmin={isAdminOrSuperAdmin}
                fetchBills={fetchBills}
              />
            </Col>
          );
        })}
      </Row>

      {/* Tus Cuentas */}
      <div className="otras-categorias-header">
        <h2 className="resumen-header">Tus Cuentas</h2>
        <Button
          className="add-category-button-Bill"
          onClick={() => setShowCreateCategory(true)}
        >
          <FaPlus size={20} />
        </Button>
      </div>

      {/* Acciones de selección */}
      {isAdminOrSuperAdmin && (
        <div className='action-button-table-container'>
          {billsList.length > 0 && (
            <Button
              variant='none'
              className="mp-button light"
              onClick={() => {
                if (showSelection) setSelectedCategories([]);
                setShowSelection(!showSelection);
              }}
            >
              {showSelection
                ? <><FaTimes /> Cancelar selección</>
                : <><FaMousePointer /> Seleccionar</>}
            </Button>
          )}
          {showSelection && selectedCategories.length > 0 && (
            <Button variant='none' className="mp-button light" onClick={handleDeleteSelectedCategories}>
              <FaTrash /> Eliminar selección ({selectedCategories.length})
            </Button>
          )}
        </div>
      )}

      {/* Categorías Locales */}
      <Row className='bill-category-row'>
        {combinedLocalCategories.map(category => {
          const id = category.local_bill_category_id;
          return (
            <Col key={id} lg={2} className="bill-category-col">
              <BillItem
                icon={localCategoryIcon}
                billsList={category}
                allBills={billsList}
                setBillsList={setBillsList}
                showSelection={showSelection}
                selectedCategories={selectedCategories}
                setSelectedCategories={setSelectedCategories}
                isAdminOrSuperAdmin={isAdminOrSuperAdmin}
                handleEditLocalCategory={handleEditLocalCategory}
                fetchBills={fetchBills}
              />
            </Col>
          );
        })}
      </Row>

      {/* Crear categoría y toasts */}
      <CreateCategoryBill
        show={showCreateCategory}
        handleClose={() => setShowCreateCategory(false)}
        handleAddCategory={handleAddLocalCategory}
      />
      <ToastContainer position="top-right" autoClose={2500} />
    </div>
  );
};

export default Resumen;
